import React, { useMemo, useState } from 'react';
import { Area, City, Locality, Outlet, Route, Warehouse } from '../../types';
import { buildAreaRoutingSummary, dryRunAreaReassignment, reassignAreaToRoute } from '../../services/areaRouting';
import { useAuth } from '../../contexts/AuthContext';

type Props = {
  areas: Area[];
  localities: Locality[];
  outlets: Outlet[];
  routes: Route[];
  warehouses: Warehouse[];
  cities: City[];
  onRefresh?: () => Promise<void> | void;
};

const AreaRoutingSection: React.FC<Props> = ({ areas, localities, outlets, routes, warehouses, cities, onRefresh }) => {
  const { currentUser } = useAuth();
  const [warehouseFilter, setWarehouseFilter] = useState<string>('');
  const [cityFilter, setCityFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [workingAreaId, setWorkingAreaId] = useState<string | null>(null);
  const [preview, setPreview] = useState<any | null>(null);
  const [previewRouteStats, setPreviewRouteStats] = useState<Record<string, { currentTotal: number; projectedTotal: number; routeName: string }>>({});
  const [previewTargetRouteId, setPreviewTargetRouteId] = useState<string | null>(null);
  const [targetRouteId, setTargetRouteId] = useState<string>('');
  const [progress, setProgress] = useState<string>('');

  const rows = useMemo(() => {
    const base = buildAreaRoutingSummary(areas, localities, outlets, routes, warehouses, cities);
    return base
      .filter(r => !warehouseFilter || r.area.warehouseId === warehouseFilter || r.warehouse?.warehouseId === warehouseFilter || r.warehouse?.id === warehouseFilter)
      .filter(r => !cityFilter || r.city?.cityCode === cityFilter)
      .filter(r => !search || r.area.name.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => a.area.name.localeCompare(b.area.name));
  }, [areas, localities, outlets, routes, warehouses, cities, warehouseFilter, cityFilter, search]);

  const allWarehouseOptions = useMemo(() => warehouses.filter(w => w.isActive), [warehouses]);
  const allCityOptions = useMemo(() => Array.from(new Set(warehouses.map(w => w.cityCode))), [warehouses]);

  const beginPreview = (areaId: string, routeId: string) => {
    const row = rows.find(r => r.area.id === areaId);
    const target = routes.find(r => r.routeId === routeId);
    if (!row || !target) return;
    try {
      const res = dryRunAreaReassignment(row.area, target, localities, outlets, routes);
      setPreview(res);
      setWorkingAreaId(areaId);
      setPreviewTargetRouteId(target.routeId!);

      // Compute global route totals (across all areas) for active outlets
      const localitiesById = new Map(localities.map(l => [l.id, l] as const));
      const activeOutlets = outlets.filter(o => o.status === 'active');
      const globalRouteCounts = new Map<string, number>();
      for (const o of activeOutlets) {
        const loc = localitiesById.get(o.localityId);
        const rid = loc?.routeId;
        if (rid) {
          globalRouteCounts.set(rid, (globalRouteCounts.get(rid) || 0) + 1);
        }
      }

      // Compute how many active outlets will move from each source route in this area
      const toUpdate = new Set<string>(res.localitiesToUpdate);
      const movedFromCounts = new Map<string, number>();
      let movedToTarget = 0;
      for (const o of activeOutlets) {
        const loc = localitiesById.get(o.localityId);
        if (!loc) continue;
        if (!toUpdate.has(loc.id)) continue; // only localities being changed
        movedToTarget++; // every active outlet in a locality being updated will move to target
        const fromRid = loc.routeId;
        if (fromRid) {
          movedFromCounts.set(fromRid, (movedFromCounts.get(fromRid) || 0) + 1);
        }
      }

      // Build per-route projection: projected = current - movedFrom + (route==target ? movedToTarget : 0)
      const stats: Record<string, { currentTotal: number; projectedTotal: number; routeName: string }> = {};
      for (const r of routes) {
        const rid = r.routeId!;
        if (!rid) continue;
        const current = globalRouteCounts.get(rid) || 0;
        const movedFrom = movedFromCounts.get(rid) || 0;
        const projected = rid === target.routeId ? current + movedToTarget : current - movedFrom;
        stats[rid] = { currentTotal: current, projectedTotal: projected, routeName: r.name };
      }
      setPreviewRouteStats(stats);
    } catch (e: any) {
      alert(e.message || 'Failed to prepare preview');
    }
  };

  const confirmReassign = async () => {
    if (!preview || !currentUser) return;
    setProgress('Starting...');
    try {
  const result = await reassignAreaToRoute(preview.areaId, { routeId: preview.targetRouteId, name: preview.targetRouteName }, currentUser.id);
      setProgress(`Updated ${result.localitiesUpdated} localities in ${result.batches} batches. Refreshing view...`);
      if (onRefresh) {
        await onRefresh();
      }
      setProgress('');
      setPreview(null);
      setWorkingAreaId(null);
    } catch (e: any) {
      setProgress('');
      // Optional: surface error inline instead of blocking alert
      console.error('Area reassignment failed:', e);
      window.alert?.(e.message || 'Reassignment failed');
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-wrap gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Warehouse</label>
          <select className="border rounded px-2 py-1 text-sm" value={warehouseFilter} onChange={e => setWarehouseFilter(e.target.value)}>
            <option value="">All</option>
            {allWarehouseOptions.map(w => (
              <option key={w.id} value={w.warehouseId}>{w.name} ({w.warehouseId})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">City</label>
          <select className="border rounded px-2 py-1 text-sm" value={cityFilter} onChange={e => setCityFilter(e.target.value)}>
            <option value="">All</option>
            {allCityOptions.map(code => (
              <option key={code} value={code}>{code}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs text-gray-500 mb-1">Search Area</label>
          <input className="border rounded px-2 py-1 text-sm w-full" value={search} onChange={e => setSearch(e.target.value)} placeholder="Type area name..." />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Area</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Warehouse</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">City</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Localities</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Outlets</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Route Mix</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Assign To Route</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map(r => {
                const eligibleRoutes = routes.filter(rt => rt.isActive && (rt.warehouseId === r.area.warehouseId || rt.warehouseId === r.warehouse?.warehouseId));
                return (
                  <tr key={r.area.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{r.area.name}</div>
                      <div className="text-xs text-gray-500">ID: {r.area.id}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-700">{r.warehouse?.name || 'Unknown'}</div>
                      {r.warehouse && (
                        <div className="text-xs text-gray-500">{r.warehouse.warehouseId}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-700">{r.city?.name || '—'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{r.localitiesCount}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{r.outletsCount}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-2">
                        {r.routeMix.length === 0 && (
                          <span className="inline-flex px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">No Route 0</span>
                        )}
                        {r.routeMix.map(mix => (
                          <span key={`${mix.routeId || 'null'}`} className="inline-flex px-2 py-1 text-xs rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                            {mix.routeName} {mix.outletCount}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <select
                          className="text-sm border border-gray-300 rounded-md px-2 py-1"
                          value={workingAreaId === r.area.id ? targetRouteId : ''}
                          onChange={(e) => { setWorkingAreaId(r.area.id); setTargetRouteId(e.target.value); }}
                        >
                          <option value="">Select Route</option>
                          {eligibleRoutes.map(rt => (
                            <option key={rt.routeId || rt.id} value={rt.routeId || ''}>{rt.name}</option>
                          ))}
                        </select>
                        <button
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
                          disabled={!targetRouteId || workingAreaId !== r.area.id}
                          onClick={() => beginPreview(r.area.id, targetRouteId)}
                        >
                          Preview
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-6 text-center text-gray-500">No areas found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6">
            <h4 className="text-lg font-semibold mb-3">Confirm Area Reassignment</h4>
            <div className="space-y-2 text-sm">
              <div><span className="text-gray-500">Localities to update:</span> {preview.localitiesToUpdate.length} / {preview.localitiesConsidered}</div>
              <div>
                <div className="text-gray-500 mb-1">Before:</div>
                <div className="flex flex-wrap gap-2">
                  {preview.beforeRouteMix.map((m: any) => (
                    <span key={m.routeId || 'none'} className="inline-flex px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                      {m.routeName} {m.outletCount}
                    </span>
                  ))}
                </div>
                {/* Show global totals and projected per contributing (from) route */}
                <div className="mt-2 grid grid-cols-1 gap-1">
                  {preview.beforeRouteMix.filter((m: any) => m.routeId).map((m: any) => {
                    const stats = previewRouteStats[m.routeId];
                    if (!stats) return null;
                    return (
                      <div key={`stats-${m.routeId}`} className="text-xs text-gray-600">
                        <span className="font-medium">{stats.routeName}</span>: Total {stats.currentTotal} → Projected {stats.projectedTotal}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">After:</div>
                <div className="flex flex-wrap gap-2">
                  {preview.afterRouteMix.map((m: any) => (
                    <span key={m.routeId} className="inline-flex px-2 py-1 text-xs rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                      {m.routeName} {m.outletCount}
                    </span>
                  ))}
                </div>
                {/* Show totals for receiving (target) route */}
                {previewTargetRouteId && previewRouteStats[previewTargetRouteId] && (
                  <div className="mt-2 text-xs text-gray-700">
                    <span className="font-medium">{previewRouteStats[previewTargetRouteId].routeName}</span>: Total {previewRouteStats[previewTargetRouteId].currentTotal} → Projected {previewRouteStats[previewTargetRouteId].projectedTotal}
                  </div>
                )}
              </div>
            </div>

            {progress && <div className="mt-3 text-xs text-gray-600">{progress}</div>}

            <div className="mt-6 flex justify-end gap-2">
              <button className="px-3 py-1 text-sm border rounded" onClick={() => { setPreview(null); setPreviewRouteStats({}); setPreviewTargetRouteId(null); setProgress(''); }}>Cancel</button>
              <button className="px-3 py-1 text-sm bg-blue-600 text-white rounded" onClick={confirmReassign}>Confirm & Reassign</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AreaRoutingSection;
